export const useAgendaId = {
  VIDE_GRENIERS: 1,
  MARCHE_POPULAIRE: 2,
  WEEK_END: 3,
  AGENDA: 4,
  SAMEDI: 5,
  AGENDA_LOTO: 6,
  BOURSE_AUX_JOUETS: 7,
  NOEL: 8,
  BOURSE_AUX_VETEMENTS: 9,
  BOURSE_PUERICULTURE: 10,
  VIDE_DRESSING: 11,
  VIDE_MAISONS: 12,
  VENTE_SOLIDAIRE: 13,
};

export const useTypeId = {
  VIDE_GRENIERS: 1,
  BROCANTES_ANTIQUAIRE: 2,
  BOURSES: 3,
  BOURSES_DE_COLLECTIONNEURS: 4,
  MARCHES_DE_NOEL: 5,
  LOTOS: 6,
  ACTIVITES_ET_COMPETITIONS_SPORTIVES: 7,
  SPECTACLES: 8,
  EVENEMENTS_FESTIFS: 9,
  ACTIVITES_CULTURELLES: 10,
  BOURSES_EXPOSITIONS_SALONS_ANIMALIERS: 11,
  VENTES_A_DOMICILE: 12,
  VENTE_SOLIDAIRE: 13,
};

export const useModuleState = {
  IS_ORGA_REQ: 1,
  IS_ORGA_OPT: 2,
  IS_PROSPECT_REQ: 4,
  IS_PROSPECT_OPT: 8,
  IS_PUBLIC_INFO: 16,
  IS_EXHIBITOR_INFO: 32,
};

export const useModule = {
  FULL: "complet",
  TAGS: "type",
  LOCATION: "location",
  CALENDAR: "calendar",
  ORGANIZER: "organizer",
  CONTACT: "contact",
  TITLE: "title",
  CAPACITY: "capacity",
  LOTO: "loto",
  PRICE: "tarif",
  DESCRIPTION: "description",
  EXHIBITOR_INFO: "informationsExposants",
  BOOKING_FILE: "ficheInscription",
  BOOKING_LINK: "bookingLink",
  PERIODICITY: "recurrence",
  PHOTOS: "photos",
  SCHEDULE: "horaires",
  SERVICES: "services",
  INSIDE: "inside",
  VIDEO: "video",
  WEBSITE: "website",
};

let pending = false;
export function useFetchAgenda(refresh = false) {
  const fetchApi = useFetchApi();

  const cache = useState("config-cache", () => null);

  if ((!cache.value || refresh) && !pending) {
    pending = true;
    fetchApi
      .get(`/v1/agenda/${useAgendaId.AGENDA}`)
      .then(({ body }) => {
        cache.value = body;
      })
      .catch(() => null)
      .finally(() => {
        pending = false;
      });
  }

  return cache;
}

export function useFetchAgendas() {
  const fetchApi = useFetchApi();

  return useAsyncData(
    "config",
    () =>
      fetchApi
        .get("/v1/agendas")
        .then(({ body }) => body)
        .catch(() => []),
    {
      transform(data) {
        const order = {
          [useAgendaId.VIDE_GRENIERS]: 1,
          [useAgendaId.VIDE_MAISONS]: 2,
          [useAgendaId.AGENDA_LOTO]: 3,
          [useAgendaId.NOEL]: 4,
          [useAgendaId.VIDE_DRESSING]: 5,
          [useAgendaId.BOURSE_AUX_VETEMENTS]: 6,
          [useAgendaId.BOURSE_PUERICULTURE]: 7,
          [useAgendaId.BOURSE_AUX_JOUETS]: 8,
          [useAgendaId.MARCHE_POPULAIRE]: 9,
          [useAgendaId.VENTE_SOLIDAIRE]: 10,
        };
        const orderObject = {};
        const noOrder = [];
        data.forEach((agenda) => {
          const index = order[agenda.id];
          if (index) {
            orderObject[index] = agenda;
          } else {
            noOrder.push(agenda);
          }
        });

        return [...Object.values(orderObject), ...noOrder];
      },
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
      },
      lazy: true,
      deep: false,
      dedupe: "defer",
      default: () => [],
    },
  );
}

export function useAllTypes(config) {
  if (!config) {
    return null;
  }
  const types = [];

  for (const type of config.types) {
    if (type.isActive) {
      types.push(type);
    }
  }
  return types;
}

export function useTagsLabels(agenda, tags) {
  if (!tags || tags.length === 0) {
    return [agenda.label];
  }

  const types = useAllTypes(agenda);
  if (!types) {
    return null;
  }

  const selectedSet = new Set(tags);

  // Tous les tags sélectionnés
  const allTags = types.flatMap((type) => type.tags);
  if (tags.length === allTags.length) {
    return [agenda.label];
  }

  const typeLabels = [];
  const tagLabels = [];

  for (const type of types) {
    if (type.tags.every((tag) => selectedSet.has(tag.id))) {
      typeLabels.push(type.labelPlurar);
    } else {
      for (const tag of type.tags) {
        if (selectedSet.has(tag.id)) {
          tagLabels.push(tag.labelPlurar);
        }
      }
    }
  }

  return [...typeLabels, ...tagLabels];
}

export function useTagsLabel(agenda, tags) {
  if (!tags || tags.length === 0) {
    return agenda.label;
  }

  const types = useAllTypes(agenda);
  if (!types) {
    return null;
  }

  const selectedSet = new Set(tags);

  // Tous les tags sélectionnés
  const allTags = types.flatMap((type) => type.tags);
  if (tags.length === allTags.length) {
    return agenda.label;
  }

  // Vérification si tous les tags d'un seul type sont sélectionnés
  const matchingType = types.find(
    (type) =>
      type.tags.length === tags.length &&
      type.tags.every((tag) => selectedSet.has(tag.id)),
  );

  if (matchingType) {
    return matchingType.labelPlurar;
  }

  // Un seul tag sélectionné
  if (tags.length === 1) {
    const tag = allTags.find((tag) => tag.id === tags[0]);
    return tag?.labelPlurar ?? null;
  }

  return `${tags.length} types d'évènements`;
}

export function usePeriodLabel(params) {
  if (!params) {
    return null;
  }

  if (params.period === dateModel.WEEK_ENDS) {
    return "Les week-ends";
  } else if (params.period === dateModel.WEEK) {
    return "La semaine prochaine";
  } else if (params.period) {
    return params.period;
  } else if (params.min || params.max) {
    const current = new Date();
    const min = new Date(params.min);
    const max = new Date(params.max);

    const nextYear = current.getFullYear() + 1;

    if (min.getFullYear() <= nextYear && max.getFullYear() <= nextYear) {
      if (
        (min.getFullYear() === current.getFullYear() &&
          max.getFullYear() === current.getFullYear()) ||
        max.getMonth() < current.getMonth()
      ) {
        if (min.getMonth() === max.getMonth()) {
          return `Du ${min.getDate()} au ${max.getDate()} ${dateModel.month[max.getMonth()].toLowerCase()}`;
        }
        return `Du ${min.getDate()} ${dateModel.month[min.getMonth()].toLowerCase()} au ${max.getDate()} ${dateModel.month[max.getMonth()].toLowerCase()}`;
      }
    }
    return `Du ${min.getDate()} ${dateModel.month[min.getMonth()].toLowerCase()} ${min.getFullYear()} au ${max.getDate()} ${dateModel.month[max.getMonth()].toLowerCase()} ${max.getFullYear()}`;
  }

  return "Prochaines dates";
}

export function useAgendaBytags(config, tags) {
  const agendas = [];

  if (!tags) {
    return [];
  }

  config.forEach((agenda) => {
    if (!agenda.isActive) {
      return;
    }
    const tagsAgenda = {};
    agenda.types.forEach((type) => {
      type.tags.forEach((tag) => {
        tagsAgenda[tag.id] = true;
      });
    });

    if (tags.some((tag) => !!tagsAgenda[tag])) {
      agendas.push(agenda);
    }
  });

  return agendas;
}

export function useTagLabel(config, tagId) {
  if (!tagId) {
    return "";
  }
  let tagAgenda = null;

  config.forEach((agenda) => {
    if (!agenda.isActive) {
      return;
    }
    agenda.types.forEach((type) => {
      type.tags.forEach((tag) => {
        if (tag.id === tagId) {
          tagAgenda = tag;
        }
      });
    });
  });

  return tagAgenda;
}

const devPorts = {
  [useAgendaId.VIDE_GRENIERS]: 8021,
  [useAgendaId.AGENDA_LOTO]: 8023,
  [useAgendaId.BOURSE_AUX_JOUETS]: 8024,
  [useAgendaId.AGENDA]: 8020,
  [useAgendaId.NOEL]: 8025,
  [useAgendaId.BOURSE_AUX_VETEMENTS]: 8026,
  [useAgendaId.BOURSE_PUERICULTURE]: 8027,
  [useAgendaId.VIDE_DRESSING]: 8028,
  [useAgendaId.VIDE_MAISONS]: 8029,
  [useAgendaId.VENTE_SOLIDAIRE]: 8030,
};

export function useAgendaBaseUrl(agenda) {
  if (!agenda) {
    return null;
  }
  switch (import.meta.env.VITE_MODE) {
    case "production":
      return `https://${agenda.domain}`;
    case "testing":
      return `https://dev.${agenda.domain}`;
    case "development":
      return devPorts[agenda.id]
        ? `http://localhost:${devPorts[agenda.id]}`
        : null;
    default:
      return null;
  }
}

export function useEventDescription(item, type, openGraph = false) {
  if (!item) {
    return "";
  }

  let description = "";

  if (!openGraph) {
    let loc = "";
    if (item.geo?.parent) {
      loc += `${item.geo.parent.name} - `;
    }
    if (item.geo?.admin) {
      loc += `${item.geo.admin.name} - `;
    }
    if (item.geo?.city) {
      loc += `${item.geo.city.name} - `;
    }

    description = `${type?.labelSingular ?? ""} ${loc}`;
  }

  for (const info of [
    item.schedule,
    item.loto,
    item.exhInfo,
    item.price,
    item.otherInfo,
  ]) {
    if (info) {
      description += ` ${info}`;
    }
  }

  return description.replaceAll("\\n", "\n");
}
